import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import type { TradFiAssumptions } from '@/types/api'

// =============================================================================
// Types
// =============================================================================

interface RegimeTemplate {
  id: string
  name: string
  description: string
  referencePeriod: string
  tradFiAssumptions: TradFiAssumptions
  // Template variables that can be overridden
  variables: RegimeVariable[]
}

interface RegimeVariable {
  key: string
  name: string
  description: string
  defaultValue: number
  unit: 'percent' | 'bps'
}

// =============================================================================
// Template Definitions (6 templates per REQUIREMENTS.md SCEN-04)
// =============================================================================

const REGIME_TEMPLATES: RegimeTemplate[] = [
  {
    id: 'risk-off-deleveraging',
    name: 'Risk-Off Deleveraging',
    description: 'Flight to safety with sharp credit spread widening and equity declines',
    referencePeriod: 'Mar 2020',
    tradFiAssumptions: {
      equityReturn: '-34.00',
      ratesChange: '-1.50',
      creditSpread: '+300',
    },
    variables: [
      { key: 'equityShock', name: 'Equity Shock', description: 'Global equity index return', defaultValue: -34, unit: 'percent' },
      { key: 'rateShock', name: 'Rate Shock', description: '10-year Treasury yield change', defaultValue: -1.5, unit: 'percent' },
      { key: 'creditSpreadShock', name: 'Credit Spread Shock', description: 'High yield credit spread widening', defaultValue: 300, unit: 'bps' },
    ],
  },
  {
    id: 'rate-hike-cycle',
    name: '2021-22 Rate Hike Cycle',
    description: 'Aggressive Fed tightening with rising rates and equity pressure',
    referencePeriod: 'Dec 2021 - Oct 2022',
    tradFiAssumptions: {
      equityReturn: '-25.00',
      ratesChange: '+4.25',
      creditSpread: '+150',
    },
    variables: [
      { key: 'equityShock', name: 'Equity Shock', description: 'Global equity index return', defaultValue: -25, unit: 'percent' },
      { key: 'rateShock', name: 'Rate Shock', description: '10-year Treasury yield change', defaultValue: 4.25, unit: 'percent' },
      { key: 'creditSpreadShock', name: 'Credit Spread Shock', description: 'High yield credit spread widening', defaultValue: 150, unit: 'bps' },
    ],
  },
  {
    id: 'crypto-contagion',
    name: 'Crypto Contagion (LUNA/3AC)',
    description: 'Crypto-specific credit crisis with limited TradFi spillover',
    referencePeriod: 'May-Jun 2022',
    tradFiAssumptions: {
      equityReturn: '-8.00',
      ratesChange: '+0.50',
      creditSpread: '+50',
    },
    variables: [
      { key: 'equityShock', name: 'Equity Shock', description: 'Global equity index return', defaultValue: -8, unit: 'percent' },
      { key: 'rateShock', name: 'Rate Shock', description: '10-year Treasury yield change', defaultValue: 0.5, unit: 'percent' },
      { key: 'creditSpreadShock', name: 'Credit Spread Shock', description: 'High yield credit spread widening', defaultValue: 50, unit: 'bps' },
    ],
  },
  {
    id: 'stagflation-light',
    name: 'Stagflation Light',
    description: 'Moderate stagflation scenario with rising rates and stagnant growth',
    referencePeriod: 'Hypothetical',
    tradFiAssumptions: {
      equityReturn: '-15.00',
      ratesChange: '+2.00',
      creditSpread: '+200',
    },
    variables: [
      { key: 'equityShock', name: 'Equity Shock', description: 'Global equity index return', defaultValue: -15, unit: 'percent' },
      { key: 'rateShock', name: 'Rate Shock', description: '10-year Treasury yield change', defaultValue: 2, unit: 'percent' },
      { key: 'creditSpreadShock', name: 'Credit Spread Shock', description: 'High yield credit spread widening', defaultValue: 200, unit: 'bps' },
    ],
  },
  {
    id: 'soft-landing',
    name: 'Soft Landing',
    description: 'Goldilocks scenario with controlled inflation and stable growth',
    referencePeriod: 'Hypothetical',
    tradFiAssumptions: {
      equityReturn: '+8.00',
      ratesChange: '-0.50',
      creditSpread: '-20',
    },
    variables: [
      { key: 'equityShock', name: 'Equity Shock', description: 'Global equity index return', defaultValue: 8, unit: 'percent' },
      { key: 'rateShock', name: 'Rate Shock', description: '10-year Treasury yield change', defaultValue: -0.5, unit: 'percent' },
      { key: 'creditSpreadShock', name: 'Credit Spread Shock', description: 'High yield credit spread widening', defaultValue: -20, unit: 'bps' },
    ],
  },
  {
    id: ' recession-2024-style',
    name: 'Recession 2024-Style',
    description: 'Modern recession with tech sector focus and policy response',
    referencePeriod: 'Hypothetical',
    tradFiAssumptions: {
      equityReturn: '-20.00',
      ratesChange: '-1.00',
      creditSpread: '+250',
    },
    variables: [
      { key: 'equityShock', name: 'Equity Shock', description: 'Global equity index return', defaultValue: -20, unit: 'percent' },
      { key: 'rateShock', name: 'Rate Shock', description: '10-year Treasury yield change', defaultValue: -1, unit: 'percent' },
      { key: 'creditSpreadShock', name: 'Credit Spread Shock', description: 'High yield credit spread widening', defaultValue: 250, unit: 'bps' },
    ],
  },
]

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch regime templates
 * Combines backend API with frontend template definitions
 */
export function useRegimeTemplates() {
  return useQuery({
    queryKey: ['regime-templates'],
    queryFn: async () => {
      // Try to fetch from backend first
      try {
        const backendTemplates = await apiClient.getRegimeTemplates()
        // Merge backend data with local definitions if needed
        return backendTemplates.length > 0 ? backendTemplates : REGIME_TEMPLATES
      } catch {
        // Fallback to static definitions
        return REGIME_TEMPLATES
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - templates rarely change
  })
}

/**
 * Get a specific template by ID
 */
export function useRegimeTemplate(templateId: string | null) {
  return useQuery({
    queryKey: ['regime-template', templateId],
    queryFn: async () => {
      if (!templateId) return null
      const templates = await apiClient.getRegimeTemplates()
      const template = templates.find((t) => t.id === templateId)
      return template || REGIME_TEMPLATES.find((t) => t.id === templateId) || null
    },
    enabled: !!templateId,
    staleTime: 1000 * 60 * 5,
  })
}

export { REGIME_TEMPLATES }
export type { RegimeTemplate, RegimeVariable }
